import { useEffect, useReducer, useState } from "react";

import { useLocalization } from "@fluent/react";
import { ComposeProviders, entities, modules } from "byzantine";
import { useNotificationContext } from "cerulean";
import { Button, Drawer, ResponsiveFlex, Row } from "@narmi/design_system";
import {
  canFinish,
  drawerReducer,
  getOpenFormSection,
  getVisibleMethods,
  hasMethod,
  hasSwiftDestinationEdits,
  initialDrawerState,
  isRequirementMet,
  useIsSwiftDestinationIncomplete,
} from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";

import { useUserFeatures } from "../../../components/contexts/UserFeaturesContext";

import ProfileSection from "./sections/profile/ProfileSection";
import PaymentMethodsSection from "./sections/paymentMethods/PaymentMethodsSection";
import ACHForm from "./sections/paymentMethods/ach/ACHForm";
import WireForm from "./sections/paymentMethods/wire/WireForm";
import InternationalWireForm from "./sections/paymentMethods/internationalWire/InternationalWireForm";

import type {
  ACHDestinationFormType,
  RecipientProfileType,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import type {
  DrawerFormSection,
  RecipientPaymentMethod,
} from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";
import type { RecipientDrawerProps } from "./types";

interface FormInitialValues {
  profile?: Partial<RecipientProfileType>;
  ach?: Partial<ACHDestinationFormType>;
}

const FORM_PROVIDERS = [
  entities.recipients.RecipientProfileForm.Provider,
  entities.recipients.ACHDestinationForm.Provider,
  entities.recipients.WireDestinationForm.Provider,
  entities.recipients.SwiftDestinationFormProvider,
];

const getFormInitialValues = (
  props: RecipientDrawerProps
): FormInitialValues => {
  const { name, type, accountNumber, routingNumber, accountType } =
    props.defaultValues ?? {};
  // the forms fall back to their own defaults for any value left undefined
  return {
    // never seed a name over a saved recipient's own
    profile:
      props.mode === "create"
        ? { name: name ?? undefined, type: type ?? undefined }
        : undefined,
    ach: {
      accountNumber: accountNumber ?? undefined,
      routingNumber: routingNumber ?? undefined,
      accountType: accountType ?? undefined,
    },
  };
};

const RecipientDrawerContent = ({
  initialValues,
  ...props
}: RecipientDrawerProps & { initialValues: FormInitialValues }) => {
  const { isOpen, flow = "settings", onClose } = props;
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const features = useUserFeatures();
  const profileForm = entities.recipients.RecipientProfileForm.useForm();
  const achForm = entities.recipients.ACHDestinationForm.useForm();
  const wireForm = entities.recipients.WireDestinationForm.useForm();
  const internationalWireForm =
    entities.recipients.useSwiftDestinationContext();
  const { internationalWires } = modules.wireTemplates.useWirePermissions();
  const [state, dispatch] = useReducer(drawerReducer, undefined, () =>
    initialDrawerState(
      flow,
      props.mode === "create"
        ? { mode: "create" }
        : { mode: "edit", recipientId: props.recipientId },
      "perSection"
    )
  );
  const recipientId = state.recipientId ?? undefined;
  const recipient = entities.recipients.useRecipient(recipientId);
  const dirtyForms: Partial<Record<DrawerFormSection, boolean>> = {
    profile: profileForm.dirty,
    ach: achForm.dirty,
    wire: wireForm.dirty,
    // ignores the fields the bank lookups set
    internationalWire: hasSwiftDestinationEdits(
      internationalWireForm.formState.dirtyFields
    ),
  };
  const isInternationalWireIncomplete = useIsSwiftDestinationIncomplete(
    state.mode === "edit" && internationalWires
      ? recipient?.swift_destination
      : null
  );

  // edit drawer opens saved international wire details that fail validation,
  // unless the flow already opened a payment method or the user has started typing
  useEffect(() => {
    if (
      !isInternationalWireIncomplete ||
      state.mode === "view" ||
      state.openMethod
    ) {
      return;
    }
    const openForm = getOpenFormSection(state);
    if (!openForm || !dirtyForms[openForm]) {
      dispatch({
        type: "methodOpenRequested",
        method: "internationalWire",
        isOpenFormDirty: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternationalWireIncomplete]);

  // the per-section drawer never moves to view mode
  if (state.mode === "view") return null;

  const isCreate = state.mode === "create";
  const isProfilePending = isCreate && !state.profileDone;
  const openForm = getOpenFormSection(state);
  const isOpenFormDirty = openForm ? Boolean(dirtyForms[openForm]) : false;
  // the flow needs this method and the recipient doesn't have it yet
  const isMissingMethod = (method: RecipientPaymentMethod) =>
    flow === method && !(recipient && hasMethod(recipient, method));
  const visibleMethods = getVisibleMethods({
    ach: Boolean(features?.ach_payments),
    wire: Boolean(features?.wires),
    internationalWire: internationalWires,
  });

  const handleFinish = () => {
    onClose({
      recipient: recipient ?? null,
      created: isCreate && Boolean(state.recipientId),
      requirementMet: recipient ? isRequirementMet(recipient, flow) : false,
    });
    if (state.hasSaved) {
      sendNotification({
        type: "success",
        text: l10n.getString(
          isCreate
            ? "recipient-drawer-saved-toast"
            : "recipient-drawer-updated-toast"
        ),
      });
    }
  };

  const getSubtitle = () => {
    if (flow === "internationalWire") {
      return l10n.getString("add-international-wire-recipient-drawer-subtitle");
    }
    return l10n.getString(
      isCreate
        ? "recipient-drawer-create-subtitle"
        : "edit-recipient-drawer-subtitle"
    );
  };

  const toggleProfile = (isDirty: boolean) =>
    dispatch({
      type: "sectionToggleRequested",
      section: "profile",
      isOpenFormDirty: isDirty,
    });

  return (
    <Drawer
      depth="633px"
      isOpen={isOpen}
      onUserDismiss={handleFinish}
      showControls={false}
      paddingSize="xl"
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              testId="cancel-recipient-drawer"
              onClick={handleFinish}
              label={l10n.getString("button-cancel")}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              testId="done-recipient-drawer"
              onClick={handleFinish}
              label={l10n.getString("recipient-drawer-done-button")}
              disabled={!canFinish(state, recipient, isOpenFormDirty)}
            />
          </Row.Item>
        </Row>
      }
    >
      <ResponsiveFlex gapSize="s">
        <h2>
          {l10n.getString(
            isCreate
              ? "recipient-drawer-create-title"
              : "edit-recipient-drawer-title"
          )}
        </h2>
        <p>{getSubtitle()}</p>
        <ProfileSection
          mode={state.mode}
          recipientId={recipientId}
          initialValues={initialValues.profile}
          isOpen={state.expandedSections.profile}
          isComplete={isCreate && state.profileDone}
          showUnsavedError={state.unsavedError === "profile"}
          needsReview={Boolean(props.reviewDefaultValues)}
          onToggle={() => toggleProfile(isOpenFormDirty)}
          onSaved={(savedRecipientId) =>
            dispatch({ type: "profileSaved", recipientId: savedRecipientId })
          }
          // with per-section saves there's nothing to cancel back to until the recipient exists
          showCancel={state.saveModel === "single" || !isProfilePending}
          // Cancel discards the input; it keeps Profile open until the recipient exists
          onCancel={() => {
            if (!isProfilePending) toggleProfile(false);
          }}
        />
        <PaymentMethodsSection
          stepNumber={isCreate ? 2 : undefined}
          flow={flow}
          recipient={recipient}
          methods={visibleMethods}
          // the default values only fill ACH
          methodsToReview={props.reviewDefaultValues ? ["ach"] : []}
          isOpen={state.expandedSections.paymentMethods}
          isLocked={isProfilePending}
          openMethod={state.openMethod}
          onToggle={() =>
            dispatch({
              type: "sectionToggleRequested",
              section: "paymentMethods",
              isOpenFormDirty,
            })
          }
          onMethodOpen={(method) =>
            dispatch({ type: "methodOpenRequested", method, isOpenFormDirty })
          }
          renderForm={(method) => {
            if (method === "internationalWire") {
              return (
                <InternationalWireForm
                  recipientId={recipientId}
                  showMissingDetailsAlert={isMissingMethod("internationalWire")}
                  showUnsavedError={state.unsavedError === "internationalWire"}
                  onSaved={() => dispatch({ type: "methodSaved" })}
                  onCancel={() => dispatch({ type: "methodCancelled" })}
                />
              );
            }
            return method === "wire" ? (
              <WireForm
                recipientId={recipientId}
                showMissingDetailsAlert={isMissingMethod("wire")}
                showUnsavedError={state.unsavedError === "wire"}
                onSaved={() => dispatch({ type: "methodSaved" })}
                onCancel={() => dispatch({ type: "methodCancelled" })}
              />
            ) : (
              <ACHForm
                recipientId={recipientId}
                initialValues={initialValues.ach}
                showMissingDetailsAlert={isMissingMethod("ach")}
                showUnsavedError={state.unsavedError === "ach"}
                onSaved={() => dispatch({ type: "methodSaved" })}
                onCancel={() => dispatch({ type: "methodCancelled" })}
              />
            );
          }}
        />
      </ResponsiveFlex>
    </Drawer>
  );
};

// one session per open: the forms and the reducer start fresh every time the drawer opens
const RecipientDrawerSession = (props: RecipientDrawerProps) => {
  // built once per session: the form hooks reset whenever this object changes, so a new one each render would wipe the user's input
  const [initialValues] = useState(() => getFormInitialValues(props));
  return (
    <ComposeProviders components={FORM_PROVIDERS}>
      <RecipientDrawerContent {...props} initialValues={initialValues} />
    </ComposeProviders>
  );
};

const RecipientDrawer = (props: RecipientDrawerProps) => {
  const { isOpen } = props;
  // following the React docs, a new key resets all state, so every open starts a new drawer session
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setSession((current) => current + 1);
  }
  return <RecipientDrawerSession key={session} {...props} />;
};

export default RecipientDrawer;
